import { Button, Card, CardContent, CardHeader, CardTitle, Input, Label, Textarea } from "@repo/ui";
import { useMutation } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";

import { parsePasted } from "@/lib/yumlab-parser";

export default function YumlabNew() {
  const navigate = useNavigate();
  const create = useMutation({
    mutationFn: async (payload: unknown) => {
      const r = await fetch("/api/recipes", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
      if (!r.ok) throw new Error("Failed to create");
      return r.json() as Promise<{ id: number }>;
    },
    onSuccess: (d) => navigate(`/recipes/${d.id}`),
  });

  const onManual = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const fd = new FormData(e.currentTarget);
    const title = (fd.get("title") as string) || "Untitled";
    const servings = Number(fd.get("servings") || 1);
    const image = (fd.get("image") as string) || undefined;
    const ingredientsText = (fd.get("ingredients") as string) || "";
    const stepsText = (fd.get("steps") as string) || "";

    const ingredients = ingredientsText
      .split(/\r?\n/)
      .map((l) => l.trim())
      .filter(Boolean)
      .map((line) => ({ name: line }));

    const steps = stepsText
      .split(/\r?\n/)
      .map((l) => l.trim())
      .filter(Boolean)
      .map((text) => ({ text }));

    create.mutate({ title, servings, image, ingredients, steps });
  };

  const onPaste = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const fd = new FormData(e.currentTarget);
    const text = (fd.get("pasted") as string) || "";
    const parsed = parsePasted(text);
    create.mutate(parsed);
  };

  return (
    <div className="min-h-screen bg-background text-ink">
      <div className="mx-auto max-w-4xl px-4 py-8 space-y-8">
        <header className="flex items-center justify-between">
          <h1 className="font-display text-2xl">New recipe</h1>
        </header>

        <div className="grid md:grid-cols-2 gap-6">
          <Card className="rounded-2xl">
            <CardHeader>
              <CardTitle className="font-display">Manual entry</CardTitle>
            </CardHeader>
            <CardContent>
              <form onSubmit={onManual} className="space-y-4">
                <div>
                  <Label htmlFor="title">Title</Label>
                  <Input id="title" name="title" required className="rounded-xl" />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="servings">Servings</Label>
                    <Input
                      id="servings"
                      name="servings"
                      type="number"
                      min={1}
                      defaultValue={1}
                      className="rounded-xl"
                    />
                  </div>
                  <div>
                    <Label htmlFor="image">Image URL (optional)</Label>
                    <Input id="image" name="image" className="rounded-xl" />
                  </div>
                </div>
                <div>
                  <Label htmlFor="ingredients">Ingredients (one per line)</Label>
                  <Textarea id="ingredients" name="ingredients" rows={6} className="rounded-xl" />
                </div>
                <div>
                  <Label htmlFor="steps">Steps (one per line)</Label>
                  <Textarea id="steps" name="steps" rows={6} className="rounded-xl" />
                </div>
                <Button
                  type="submit"
                  className="cartoony-btn rounded-full bg-mint text-ink hover:bg-mint/90"
                >
                  Create
                </Button>
              </form>
            </CardContent>
          </Card>

          <Card className="rounded-2xl">
            <CardHeader>
              <CardTitle className="font-display">Paste text</CardTitle>
            </CardHeader>
            <CardContent>
              <form onSubmit={onPaste} className="space-y-4">
                <div>
                  <Label htmlFor="pasted">Paste a recipe (with Ingredients: and Steps:)</Label>
                  <Textarea
                    id="pasted"
                    name="pasted"
                    rows={16}
                    className="rounded-xl"
                    placeholder={
                      "Title: My Pasta\n\nIngredients:\n- 200 g spaghetti\n- 2 cloves garlic\n\nSteps:\n1. Boil pasta (10 min)\n2. Saute garlic 2 min"
                    }
                  />
                </div>
                <Button
                  type="submit"
                  className="cartoony-btn rounded-full bg-bubblegum text-ink hover:bg-bubblegum/90"
                >
                  Parse & Create
                </Button>
              </form>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}
